import type { AuthTokens } from '@fdt/shared';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { tokenStorage } from '../auth/token-storage';

const baseURL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api`;

export const api = axios.create({ baseURL, timeout: 20_000 });

/** Error body shape produced by the API's exception filter. */
export interface ApiErrorBody {
  statusCode: number;
  messageKey: string;
  details?: { fieldErrors?: { field: string; messageKey: string }[] };
}

/** The i18n key for an error, falling back to a generic one. */
export function errorKey(error: unknown): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    if (!error.response) {
      return 'errors.network';
    }
    return error.response.data?.messageKey ?? 'errors.unexpected';
  }
  return 'errors.unexpected';
}

api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Called when refreshing fails, so the app can send the user back to login. */
let onSessionLost: () => void = () => undefined;
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

/**
 * Concurrent refreshes must not each fire their own request. Rotation
 * invalidates the token it consumed, so a second call with the same token looks
 * like a replayed one and the server revokes every session for that user — the
 * correct server behaviour, and fatal if the client duplicates the call.
 *
 * It duplicates it easily: two 401s in flight at once, or React StrictMode
 * running the session-restore effect twice in development. Everything therefore
 * goes through this one promise.
 */
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('no-refresh-token');
  }

  const { data } = await axios.post<AuthTokens>(`${baseURL}/auth/refresh`, { refreshToken });
  tokenStorage.setAccessToken(data.accessToken);
  tokenStorage.setRefreshToken(data.refreshToken);
  return data.accessToken;
}

/**
 * Single-flight refresh, shared by the 401 interceptor and session restore.
 * Resolves with the new access token.
 */
export function refreshSession(): Promise<string> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const request = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

    const isAuthRoute = request?.url?.includes('/auth/');
    if (error.response?.status !== 401 || !request || request._retried || isAuthRoute) {
      throw error;
    }

    request._retried = true;

    try {
      const token = await refreshSession();
      request.headers = { ...request.headers, Authorization: `Bearer ${token}` };
      return await api.request(request);
    } catch {
      tokenStorage.clear();
      onSessionLost();
      throw error;
    }
  },
);
